/**
 * Chat Skeleton Components
 * Provides loading placeholders that match the actual chat UI structure
 * Better UX than generic spinners - reduces perceived loading time
 */

import React from 'react';
import { cn } from '../../../../lib/utils';

/**
 * Base skeleton pulse animation class
 */
const skeletonBase = 'animate-pulse bg-gray-200 dark:bg-gray-700 rounded-sm';

/**
 * Message skeleton props
 */
interface MessageSkeletonProps {
  /** Whether this is a user message (right aligned) or bot message (left aligned) */
  isUser?: boolean;
  /** Number of text lines to show */
  lines?: number;
  /** Optional className */
  className?: string;
}

/**
 * Skeleton for a single chat message
 * Matches the actual ChatMessage component structure
 */
export const MessageSkeleton: React.FC<MessageSkeletonProps> = ({
  isUser = false,
  lines = 3,
  className,
}) => {
  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row',
        className
      )}
      aria-hidden="true"
    >
      {/* Avatar skeleton */}
      <div
        className={cn(
          skeletonBase,
          'w-8 h-8 rounded-full shrink-0'
        )}
      />

      {/* Message bubble skeleton */}
      <div
        className={cn(
          'flex-1 max-w-[80%] space-y-2 p-4 rounded-2xl',
          isUser
            ? 'bg-emerald-50 dark:bg-emerald-900/20'
            : 'bg-gray-50 dark:bg-gray-800'
        )}
      >
        {/* Text lines with varying widths */}
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={cn(
              skeletonBase,
              'h-4',
              index === lines - 1 ? 'w-3/4' : 'w-full'
            )}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Skeleton for loading conversation history
 * Shows multiple message skeletons with alternating alignment
 */
export const ConversationSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => {
  return (
    <div
      className={cn('space-y-4 p-4', className)}
      role="status"
      aria-label="Loading conversation"
    >
      <span className="sr-only">Loading conversation...</span>
      <MessageSkeleton isUser={false} lines={2} />
      <MessageSkeleton isUser={true} lines={1} />
      <MessageSkeleton isUser={false} lines={4} />
      <MessageSkeleton isUser={true} lines={2} />
    </div>
  );
};

/**
 * Skeleton for conversation list item in history
 */
export const ConversationListItemSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => {
  return (
    <div
      className={cn(
        'p-4 rounded-xl bg-white dark:bg-gray-800',
        'border border-gray-200/40 dark:border-gray-700/40',
        className
      )}
      aria-hidden="true"
    >
      <div className="space-y-2">
        {/* Title */}
        <div className={cn(skeletonBase, 'h-4 w-3/4')} />
        {/* Preview text */}
        <div className={cn(skeletonBase, 'h-3 w-full')} />
        {/* Timestamp */}
        <div className={cn(skeletonBase, 'h-3 w-1/4')} />
      </div>
    </div>
  );
};

/**
 * Skeleton for conversation history list
 */
export const ConversationHistorySkeleton: React.FC<{ count?: number; className?: string }> = ({
  count = 4,
  className,
}) => {
  return (
    <div
      className={cn('space-y-3', className)}
      role="status"
      aria-label="Loading conversation history"
    >
      <span className="sr-only">Loading conversation history...</span>
      {/* Date group header */}
      <div className={cn(skeletonBase, 'h-3 w-16 mb-4')} />
      {/* Conversation items */}
      {Array.from({ length: count }).map((_, index) => (
        <ConversationListItemSkeleton key={index} />
      ))}
    </div>
  );
};

/**
 * Inline loading indicator for within messages
 */
export const InlineLoadingSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => {
  return (
    <div
      className={cn('flex items-center gap-1', className)}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
      <div className={cn(skeletonBase, 'w-2 h-2 rounded-full')} style={{ animationDelay: '0ms' }} />
      <div className={cn(skeletonBase, 'w-2 h-2 rounded-full')} style={{ animationDelay: '150ms' }} />
      <div className={cn(skeletonBase, 'w-2 h-2 rounded-full')} style={{ animationDelay: '300ms' }} />
    </div>
  );
};

export default {
  MessageSkeleton,
  ConversationSkeleton,
  ConversationListItemSkeleton,
  ConversationHistorySkeleton,
  InlineLoadingSkeleton,
};
